import { supabase } from './supabaseClient';

const REPORTER_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/error-reporter`;

// Each app sets this via installGlobalErrorInterceptors('houseme')
let _appName = 'unknown';

// Guard: prevents recursive reporting (errorReporter catching its own errors)
let _isReporting = false;

/** Returns true for errors that are purely connectivity-related (offline, DNS failure, etc.) */
function isConnectivityError(message: string): boolean {
  return (
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    message.includes('ERR_NAME_NOT_RESOLVED') ||
    message.includes('ERR_INTERNET_DISCONNECTED') ||
    message.includes('ERR_NETWORK') ||
    message.includes('net::ERR_') ||
    message.includes('Load failed') ||
    message.includes('Network request failed')
  );
}

interface ErrorPayload {
  page_route?: string;
  component?: string;
  action?: string;
  error_message: string;
  error_type?: 'runtime' | 'network' | 'render' |
    'unhandled_promise' | 'console_error' | 'resource';
  error_code?: string;
  error_level?: string;
  user_message?: string;
  context_data?: object;
}

async function getUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id ?? null;
  } catch {
    return null;
  }
}

function getDeviceInfo() {
  return {
    platform: navigator.platform,
    language: navigator.language,
    online: navigator.onLine,
    screen: `${screen.width}x${screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    touch: 'ontouchstart' in window,
  };
}

// Queue for offline errors
const errorQueue: ErrorPayload[] = [];
let isFlushing = false;

async function flushQueue() {
  if (isFlushing || errorQueue.length === 0) return;
  isFlushing = true;

  while (errorQueue.length > 0) {
    const err = errorQueue.shift()!;
    await reportError(err).catch(() => {});
  }

  isFlushing = false;
}

export async function reportError(payload: ErrorPayload) {
  // Never report when offline — queue for later
  if (!navigator.onLine) {
    if (errorQueue.length < 50) errorQueue.push(payload);
    return;
  }

  // Prevent recursive reporting (error reporter catching its own fetch errors)
  if (_isReporting) return;

  // Skip connectivity errors entirely — they're expected when offline and will loop
  if (isConnectivityError(payload.error_message)) return;

  _isReporting = true;
  try {
    // Use cached session from localStorage — no network call needed
    const userId = await getUserId().catch(() => null);
    const sessionData = await supabase.auth.getSession().catch(() => ({ data: { session: null } }));
    const session = sessionData?.data?.session ?? null;

    const body = {
      ...payload,
      app_name: _appName,
      user_id: userId,
      session_id: session?.access_token?.slice(-8) ?? null,
      page_route: payload.page_route ?? window.location.pathname,
      device_info: getDeviceInfo(),
      app_version: import.meta.env.VITE_APP_VERSION ?? '1.0.0',
      user_agent: navigator.userAgent.slice(0, 200),
    };

    await fetch(REPORTER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

  } catch {
    // The reporter must never cause more errors
    // Silently swallow
  } finally {
    _isReporting = false;
  }
}

/**
 * Install all global error interceptors.
 * Call once in each app's main.tsx BEFORE createRoot.
 */
export function installGlobalErrorInterceptors(appName: string = 'unknown') {
  _appName = appName;
  // 1. Uncaught JS errors
  window.addEventListener('error', (event) => {
    if (event.filename?.includes('extension://')) return;

    reportError({
      error_message: event.message,
      error_type: 'runtime',
      error_code: `RUNTIME-${(event.error as Error)?.name ?? 'Error'}`,
      component: event.filename?.split('/').pop() ?? 'unknown',
      page_route: window.location.pathname,
      action: 'unknown',
      context_data: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: (event.error as Error)?.stack?.slice(0, 500),
      },
    });
  });

  // 2. Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error
      ? reason.message
      : String(reason);

    if (message.includes('AbortError') ||
        isConnectivityError(message)) return;

    reportError({
      error_message: message,
      error_type: 'unhandled_promise',
      error_code: `PROMISE-${reason?.name ?? 'Rejection'}`,
      page_route: window.location.pathname,
      action: 'async_operation',
      context_data: {
        stack: reason?.stack?.slice(0, 500),
        type: typeof reason,
      },
    });
  });

  // 3. Resource load failures (images, scripts, CSS) — capture phase
  window.addEventListener('error', (event) => {
    const target = event.target as HTMLElement;
    if (!target || target === window as unknown) return;

    const tag = target.tagName?.toLowerCase();
    if (!tag || !['img', 'script', 'link', 'video'].includes(tag)) return;

    const src = (target as HTMLImageElement).src ??
                (target as HTMLLinkElement).href ?? '';

    if (!src.includes(window.location.hostname) &&
        !src.includes('supabase')) return;

    reportError({
      error_message: `Resource failed to load: ${src.slice(-80)}`,
      error_type: 'resource',
      error_code: `RESOURCE-${tag.toUpperCase()}`,
      page_route: window.location.pathname,
      action: `load_${tag}`,
      context_data: { src, tag },
    });
  }, true);

  // 4. Fetch interceptor for 5xx from internal APIs
  const originalFetch = window.fetch;
  window.fetch = async function (...args: Parameters<typeof fetch>) {
    try {
      const response = await originalFetch.apply(this, args);

      const url = String(args[0]);
      const isInternal = url.includes('supabase.co') ||
                         url.includes('elfom.com') ||
                         url.includes('houseme.app');

      if (isInternal && response.status >= 500) {
        reportError({
          error_message: `API Error ${response.status}: ${url.slice(-80)}`,
          error_type: 'network',
          error_code: `HTTP-${response.status}`,
          page_route: window.location.pathname,
          action: 'fetch',
          context_data: {
            url: url.slice(-120),
            status: response.status,
          },
        });
      }

      return response;
    } catch (error: unknown) {
      const err = error as Error;
      const msg = err.message ?? '';
      // Skip abort errors and connectivity errors (offline/DNS) — they loop and are expected
      if (!msg.includes('AbortError') && !isConnectivityError(msg)) {
        reportError({
          error_message: `Network Error: ${msg}`,
          error_type: 'network',
          error_code: 'NETWORK-FETCH',
          page_route: window.location.pathname,
          action: 'fetch',
          context_data: { url: String(args[0]).slice(-120) },
        });
      }
      throw error;
    }
  };

  // Flush queued errors when back online
  window.addEventListener('online', flushQueue);
}
